(ns app.front.view.application.progress
  (:require [app.domain.application.shared-process :as sp]
            [app.front.process :as process]
            [app.front.ui.error-list :as ui-error-list]
            [app.front.view.application.work-type-target :as work-select]
            [app.front.view.fee.list :as fee-list]
            [app.front.view.workspace.action-subs :as action-subs]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


;; Guards don't have any context information with which to tell, which guard relates to
;; which section on the UI
(def party-error-ids #{:process.guard/required-party-roles
                       :process.guard/required-party-roles-except-deferred})
(def fees-and-payments-error-ids #{:process.guard/deposit-paid-or-not-required?})


(rf/reg-sub ::visible-errors-for-the-signal
  :<- [::process/application-process]
  (fn [process [_ signal-id]]
    (->> (sp/visible-errors-for-signal process signal-id)
         distinct
         (map (fn [[guard-id _]] guard-id)))))


(rf/reg-sub ::signal-allowed-status
  :<- [::process/application-process]
  (fn [process [_ signal-id]]
    (first (sp/signal-allowed-status process signal-id))))


(defn infos [[actions tr]]
  (let [{:keys [primary secondary]} (group-by :ui-type actions)
        buttons                     (remove nil? (concat secondary primary))]
    (->> buttons
         (map (p/fn-> :primary-action :info vector tr))
         (remove nil?))))

(rf/reg-sub ::infos
  :<- [::action-subs/actions [::process/application-process]]
  :<- [:tr]
  infos)


(rf/reg-sub ::application-errors?
  :<- [::action-subs/all-errors [::process/application-process]]
  :<- [::infos]
  (fn [[all-errors infos] _]
    (or (not-empty all-errors)
        (seq infos))))


(rf/reg-sub ::party-errors
  :<- [::action-subs/all-error-ids [::process/application-process]]
  (fn [error-ids]
    (set/intersection error-ids party-error-ids)))


(rf/reg-sub ::party-errors?
  :<- [::party-errors]
  (fn [errors]
    (> (count errors) 0)))


(rf/reg-sub ::party-status
  :<- [::work-select/work-type-target-set?]
  :<- [::party-errors]
  (fn [[work-type-and-target-set? errors]]
    (if (not work-type-and-target-set?)
      :unavailable
      (if (> (count errors) 0) :error :done))))


(rf/reg-sub ::application-data-status
  (fn [[_ application]]
    [(rf/subscribe [:app.front.form.subs/missing-any-data? application])
     (rf/subscribe [::work-select/work-type-target-set?])])
  (fn [[missing-data? work-type-target-set?] _]
    (if (or missing-data?
            (not work-type-target-set?))
      :error
      :done)))


(rf/reg-sub ::fees-and-payments-errors
  :<- [::action-subs/all-error-ids [::process/application-process]]
  (fn [error-ids]
    (set/intersection error-ids fees-and-payments-error-ids)))


(defn fees-and-payments-status [[invoices-paid? app-state error-ids]]
  (if (not-empty error-ids)
    :error
    (if (= app-state :state.application/permit-issued)
      :done
      (if invoices-paid? :done :unavailable))))


(rf/reg-sub ::fees-and-payments-status
  (fn [[_ application]]
    [(rf/subscribe [::fee-list/invoices-paid? (:db/id application)])
     (rf/subscribe [:app.front.view.application/application-state])
     (rf/subscribe [::fees-and-payments-errors])])
  fees-and-payments-status)


(defn render-phase-status
  "Renders a single phase status indicator"
  [count i {phase-name :name status :status}]
  (let [icon        (case status
                      :done i/check
                      :error i/circle-attention
                      :unavailable i/circle)
        color       (case status
                      :done "var(--status-success)"
                      :error "var(--status-error)"
                      :unavailable "var(--status-neutral-01)")
        description (name status)
        last?       (= i (- count 1))]
    [:div.flex.flex-1.flex-col.items-center {:key phase-name}
     [:div.w-full.mb-2.py-2.flex.flex-1.justify-center.border.border-solid.border-gray-400.bg-white
      {:class (if last? "border-r-1" "border-r-0")}
      (cc/icon {:icon        icon
                :description description
                :fill        color
                :class-name  ""})]
     [:div.font-bold.text-sm.text-center.leading-tight phase-name]]))


(defn render-application-overview [phases]
  [:div#application-overview.flex.justify-between.items-start.mb-4
   (map-indexed (partial render-phase-status (count phases)) phases)])


(defn render-application-progress
  "Renders the 'Required Tasks' bar and a list of possible errors."
  [{:keys [tr application hide-if-no-errors?]}]
  (let [errors                  (<sub [::action-subs/all-errors [::process/application-process]])
        infos                   (<sub [::infos])
        errors?                 (<sub [::application-errors?])
        party-status            (<sub [::party-status])
        application-data-status (<sub [::application-data-status application])
        attachments-status      (<sub [:app.front.view.application/attachment-status (:db/id application)])
        fees-status             (<sub [::fees-and-payments-status application])]
    (when (or (not hide-if-no-errors?)
              (seq errors)
              (seq infos)
              (some (partial = :error) [party-status
                                        application-data-status
                                        attachments-status
                                        fees-status]))
      [:div.bx--row.mt-4
       [:div#required-tasks.bx--col-xs-12.bx--col-lg-6
        (when errors?
          [:h2.uppercase.font-bold.text-base (tr [:application/required-tasks])])
        [render-application-overview [{:name (tr [:application/parties]) :status party-status}
                                      {:name (tr [:application/application]) :status application-data-status}
                                      {:name (tr [:application/attachments]) :status attachments-status}
                                      {:name (tr [:application/fees-and-payments]) :status fees-status}]]
        (when errors?
          [:p (tr [:application/incomplete-tasks-exist])])]
       [:div#error-list.bx--col-xs-12.bx--col-lg-6.flex.flex-col.pt-3
        [:<>
         [ui-error-list/signal-error-list tr errors]
         (when (seq infos)
           [:ul.mr-2.list-none
            (for [info infos]
              [:li {:key info}
               [:div.flex.items-center.mb-2
                (cc/icon {:icon        i/circle-attention
                          :description "Error"
                          :fill        "#616161"
                          :class-name  "mr-2 w-6 flex-shrink-0"})
                [:div.leading-tight.flex-shrink info]]])])]]])))
